import { useEffect, Dispatch, SetStateAction } from "react";
import { Category, ClickOffer, ListingItem } from "@lib/shared/types";
import { DomainInfo } from "src/api/domainInfo";
import { FieldData } from "../types";
import { getClicksSearchList, getListingBySlugV3 } from "src/api";

export interface UseFormSideFetchesArgs {
    domain: DomainInfo;
    category: Category | null | undefined;
    fieldsData: { [k: string]: FieldData };
    showRegulations: boolean;
    showCustomListingModal: boolean;
    listingSlug: string | null | undefined;
    setLoading: (loading: boolean) => void;
    setOffersPopupActive: Dispatch<SetStateAction<boolean>>;
    setClicksOffers: Dispatch<SetStateAction<ClickOffer[] | null | undefined>>;
    setListRequestId: Dispatch<SetStateAction<string>>;
    setPopupOffers: Dispatch<SetStateAction<ListingItem[]>>;
}

/**
 * Side-effect fetches that mirror the legacy `formNew.tsx` behavior:
 * - When `showRegulations` flips on, fetch cross-selling clicks offers and
 *   open the popup with the resulting list.
 * - When `showCustomListingModal` flips on with a `listingSlug`, fetch that
 *   listing and open the popup with its offers.
 *
 * Behavior is preserved verbatim including the existing single-dep arrays.
 */
export function useFormSideFetches({
    domain,
    category,
    fieldsData,
    showRegulations,
    showCustomListingModal,
    listingSlug,
    setLoading,
    setOffersPopupActive,
    setClicksOffers,
    setListRequestId,
    setPopupOffers,
}: UseFormSideFetchesArgs): void {
    // [SAME AS OLD FORM]
    useEffect(() => {
        const fetchInsuranceOffers = async () => {
            const crossSellingDomainFormId = domain.categories.find(
                (item) => item.slug === category?.slug,
            )?.crossSellingDomainFormId;
            if (crossSellingDomainFormId) {
                setLoading(true);
                const { data: clicksSearchList } = await getClicksSearchList({
                    questions: {
                        zipCode: {
                            value: fieldsData["zipCode"].value,
                            name: "zipCode",
                        },
                    },
                    categorySlug: "auto-insurance",
                    crossSellingDomainFormId: crossSellingDomainFormId,
                });
                setLoading(false);
                return clicksSearchList;
            }
            return [];
        };

        if (showRegulations) {
            fetchInsuranceOffers()
                .then((res) => {
                    setOffersPopupActive(true);
                    if (res && !Array.isArray(res)) {
                        setClicksOffers(res.result);
                        setListRequestId(res.listRequestId);
                    }
                })
                .catch(() => {
                    setClicksOffers([]);
                    setOffersPopupActive(false);
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showRegulations]);

    // [SAME AS OLD FORM]
    useEffect(() => {
        const fetchListingBySlug = async (slug: string) => {
            const { data } = await getListingBySlugV3({ slug });
            return data;
        };

        if (showCustomListingModal && listingSlug) {
            fetchListingBySlug(listingSlug)
                .then((res) => {
                    if (res && res.offers && res.offers.length) {
                        setOffersPopupActive(true);
                        setPopupOffers(res?.offers);
                    }
                })
                .catch(() => {
                    setPopupOffers([]);
                    setOffersPopupActive(false);
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showCustomListingModal]);
}
